/**
 * 🖼️ **화상을 우리 화면 안에서 열 수 있는가** — 끼워 넣기 판정.
 *
 * ## 왜 다시 끼워 넣나 (2026-09-03 운영자 요청)
 * 「새 페이지 뜨는 것보다 **해당 홈페이지에서 만든 것처럼**」. 맞는 요청이다 —
 * 새 창이 뜨면 아이는 우리 화면을 잃고, 카톡 인앱 브라우저에서는 아예 안 뜬다.
 *
 * ## 🔴 끼워 넣으려면 **셋이 다 맞아야** 한다
 * ① 우리 배포 헤더가 그 출처에 카메라·마이크를 **넘겨야** 한다(`Permissions-Policy`).
 *    iframe 의 `allow=` 는 **상위가 가진 것을 넘기는 것**이라 상위에서 막혔으면 소용없다
 *    (2026-09-02 사고의 정확한 원인).
 * ② iframe 에 `allow="camera; microphone"` 을 달아야 한다.
 * ③ **그 서비스가 끼워 넣기를 허용해야 한다** — `X-Frame-Options`·CSP `frame-ancestors`.
 *    이건 **우리가 못 정한다.** 8x8 은 `meet.jit.si` 끼워 넣기를 데모로만 허용해서
 *    5분 뒤 통화를 끊었다(그래서 Jitsi 를 버렸다). 네이버가 웨일온을 어떻게 두는지는
 *    코드로 알 수 없고, **열어 봐야 안다.**
 *
 * 그래서 규칙은 이렇다: **①②는 여기서 맞춰 주고, ③은 못 맞추니 항상 새 창 길을 같이 둔다.**
 * 끼운 화면이 비어 보이면 아이도 어른도 그 단추로 간다 — 막다른 길을 만들지 않는다.
 *
 * ## 아무 주소나 끼우지 않는다
 * 헤더는 **출처를 하나하나 적어야** 열린다(`*` 로 열면 그냥 구멍이다). 그래서 우리가
 * 헤더에 적어 둔 출처만 끼운다. 목록에 없는 서비스는 **새 창으로만** 연다 —
 * 끼워 봤자 카메라가 안 켜져서 「되는 척하다 안 되는」 화면이 된다.
 */

/**
 * 우리 화면 안에서 열어 줄 화상 서비스 출처.
 *
 * 🔴 **여기를 늘리면 `apps/web/public/_headers` 의 `Permissions-Policy` 도 같이 늘려야 한다.**
 * `packages/shared/src/headersFile.test.ts` 가 양쪽을 견준다 — 한쪽만 고치면 거기서 걸린다.
 */
export interface MeetService {
  /** 출처. `https://` 로 시작해야 한다. */
  origin: string;
  /**
   * 🔴 **이 서비스가 끼워 넣기를 허용하나** — 우리가 못 정하는 쪽(위 ③).
   * 값은 **실측**이다. 지어내지 말 것 — `curl -D -` 로 `x-frame-options` 와
   * CSP `frame-ancestors` 를 보면 1분에 안다.
   */
  embed: boolean;
  /** 왜 그렇게 정했나 — 나중에 되돌리려는 사람에게 근거를 남긴다. */
  why: string;
}

/**
 * 우리가 아는 화상 서비스. **여는 건 다 되고, 끼우는 건 `embed` 인 것만** 된다.
 *
 * 🔴 **`embed` 를 늘리면 `apps/web/public/_headers` 의 `Permissions-Policy` 도 같이
 * 늘려야 한다.** `packages/shared/src/headersFile.test.ts` 가 양쪽을 견준다.
 */
export const MEET_SERVICES: readonly MeetService[] = [
  {
    /**
     * 🔴 한때 여기를 `on.whale.naver.com` 으로 적어 뒀다 — **없는 도메인**이다(NXDOMAIN).
     * 그래서 운영자가 그 주소로 가서 방을 못 만들고 「없는 주소」만 봤다(2026-09-03).
     * 글자로는 안 보인다 — `meetHostAlive.test.ts` 가 **진짜 살아 있는지 물어본다**.
     */
    origin: "https://whaleon.naver.com",
    embed: true,
    why: "2026-09-03 실측: X-Frame-Options 도 frame-ancestors 도 없다",
  },
  {
    /**
     * 🔴 **진짜 초대 링크는 여기다**(2026-09-03 운영자 화면에서 확인):
     *   `https://whaleon.us/o/<방번호>/<초대코드>`
     * 방을 만드는 곳은 `whaleon.naver.com/my` 인데 **나눠 주는 주소는 다른 출처**다 —
     * 한 개만 적어 두면 아이 화면에서 **카메라가 조용히 안 켜진다.**
     */
    origin: "https://whaleon.us",
    embed: true,
    why: "2026-09-03 실측: 끼워 넣기를 막는 헤더가 없다",
  },
  {
    /**
     * 🔴 **구글밋은 절대 못 끼운다.** 실측(2026-09-03):
     *
     *     x-frame-options: DENY
     *
     * 그런데 우리는 이걸 「끼울 수 있는 곳」에 넣어 두고 있었다 — 그러면 화면은
     * **영영 비어 있는 iframe** 을 그린다. 아래 「비어 보이면 새 창으로」 안내가 있어
     * 막다른 길은 아니지만, **처음부터 안 될 걸 알면서 빈 칸을 보여 준 것**이다.
     * 아는 것을 화면이 모르는 척하면 안 된다 — 구글밋은 처음부터 새 창으로 연다.
     *
     * 목록에는 **남긴다.** 운영자가 붙여넣고 [🔎 확인] 을 눌렀을 때 서버가 열어 볼 수
     * 있어야 하고(SSRF 허용목록), 「모르는 서비스」로 잘못 말하면 안 되기 때문이다.
     */
    origin: "https://meet.google.com",
    embed: false,
    why: "2026-09-03 실측: x-frame-options: DENY — 끼우면 영영 빈 화면이다",
  },
  {
    /**
     * 🔴 **Jitsi 는 끼우지 않는다 — 헤더로는 안 보이는 이유다.**
     * 헤더만 보면 막혀 있지 않다. 그런데 8x8 은 `meet.jit.si` 를 **끼워 쓰는 것**을
     * 데모로만 허용해서, 끼우면 **통화가 5분 뒤 끊긴다**(2026-09-02 운영자가 행사 중에
     * 그 안내를 받았다). 새 창으로 여는 건 그런 제한이 없다.
     *
     * 그래서 실측 검사도 「막혔는데 끼운다」만 잡고 그 반대는 안 잡는다 —
     * **이 칸이 바로 그 반대의 경우**다.
     */
    origin: "https://meet.jit.si",
    embed: false,
    why: "8x8 이 끼워 쓰는 것을 데모로만 허용 — 끼우면 5분 뒤 끊긴다(헤더로는 안 보인다)",
  },
  {
    origin: "https://zoom.us",
    embed: true,
    why: "2026-09-03 실측: 끼워 넣기를 막는 헤더가 없다",
  },
  {
    origin: "https://us02web.zoom.us",
    embed: true,
    why: "2026-09-03 실측: zoom.us 와 같다",
  },
];

/**
 * 우리 화면 **안에서** 열어 줄 출처 — `Permissions-Policy` 와 **정확히 같아야** 하는 목록.
 * 여기 없는 서비스는 새 창으로만 연다(끼워 봤자 빈 화면이라).
 */
export const MEET_EMBED_ORIGINS = MEET_SERVICES.filter((s) => s.embed).map((s) => s.origin);

/**
 * 우리가 **아는** 화상 서비스 전부 — 끼우진 못해도 여기까진 안다.
 * 🔴 서버가 [🔎 확인] 으로 열어 보는 허용목록이 이것이다(끼움 여부와 **다른 질문**이다).
 */
export const MEET_KNOWN_ORIGINS = MEET_SERVICES.map((s) => s.origin);

/** 이 주소를 우리 화면 안에서 열어 볼 수 있나(= 헤더가 그 출처에 카메라를 넘겼나). */
export function canEmbedMeet(url: string | undefined | null): boolean {
  return meetOrigin(url) !== null;
}

/** 이 주소의 출처가 우리가 여는 목록에 있으면 그 출처, 아니면 `null`. */
export function meetOrigin(url: string | undefined | null): string | null {
  const s = (url ?? "").trim();
  if (!s) return null;
  try {
    const o = new URL(s).origin;
    return (MEET_EMBED_ORIGINS as readonly string[]).includes(o) ? o : null;
  } catch {
    return null; // 주소 모양이 아니면 끼우지 않는다
  }
}

/**
 * 끼운 화면 아래에 늘 두는 말. **비어 보일 수 있다는 걸 미리 말해 둔다** —
 * 그 서비스가 끼워 넣기를 막으면 화면이 하얗게 뜨는데, 그때 아무 말도 없으면 고장으로 보인다.
 */
export const MEET_EMBED_FALLBACK_SAY =
  "화면이 비어 보이면 이 서비스가 끼워 넣기를 막은 거예요 — 아래 단추로 새 창에서 열어 주세요.";

/** 이 주소가 **우리가 아는** 화상 서비스인가 — 끼움 여부와 다른 질문이다(서버 확인용). */
export function isKnownMeetOrigin(url: string | undefined | null): boolean {
  const s = (url ?? "").trim();
  if (!s) return false;
  try {
    return MEET_KNOWN_ORIGINS.includes(new URL(s).origin);
  } catch {
    return false;
  }
}

/** 끼울 수 없는 서비스일 때 운영자에게 할 말(관리자에서 쓴다). */
export function meetEmbedSay(url: string | undefined | null): string | null {
  if (!url?.trim()) return null;
  if (canEmbedMeet(url)) return null;
  /**
   * 🔴 **아는 서비스인데 못 끼우는 것**과 **모르는 서비스**는 다른 일이다.
   * 구글밋은 우리가 알고 잘 열어 주는데 **그쪽이 끼워 넣기를 막는다**(x-frame-options: DENY).
   * 그걸 「모르는 주소」처럼 말하면 운영자는 멀쩡한 링크를 의심하고 바꾸려 든다.
   */
  const known = MEET_SERVICES.find((s) => {
    try {
      return s.origin === new URL(url).origin;
    } catch {
      return false;
    }
  });
  if (known) {
    return `${new URL(known.origin).host} 는 새 창으로 열려요 — 그쪽이 끼워 넣기를 막아 둬서예요(우리가 못 바꿔요).`;
  }
  return (
    "이 주소는 우리 화면 안에서 못 열어요(새 창으로 열려요). " +
    `화면 안에서 열리는 건 ${MEET_EMBED_ORIGINS.map((o) => new URL(o).host).join(" · ")} 예요.`
  );
}

/**
 * 🏠 **기본 회의 주소** — 행사·수업에 따로 안 넣으면 여기로 연다.
 *
 * ## 왜 (2026-09-03 운영자 요청: 「웨일 방 만들기를 자동화 가능해?」)
 * 자동화는 **못 한다.** 웨일온은 네이버 로그인이 있어야 방이 만들어지고 공개 API 가 없다 —
 * 서버가 로그인 세션을 흉내 내려면 비밀번호를 두어야 하고(불변식 1 과 충돌), 네이버가
 * 화면을 바꾸는 날 깨진다.
 *
 * 대신 **매번 만들지 않게** 한다: 방 하나를 만들어 여기 넣어 두면 행사·수업이 그걸 쓴다.
 * 행사마다 다른 방을 쓰고 싶으면 그 행사에만 넣으면 된다 — **각자 넣은 것이 이긴다.**
 *
 * 🔴 **폴백이지 「그래도 뭐라도 열리게」가 아니다.** 둘 다 없으면 `null` 이고 화면은
 * 「아직 준비 안 됐다」고 말한다 — Jitsi 자동 방으로 돌아가지 않는다(5분 뒤 끊긴다).
 */
export function meetUrlOrDefault(
  own: string | undefined | null,
  siteDefault: string | undefined | null,
): string | null {
  return own?.trim() || siteDefault?.trim() || null;
}

/** 지금 이 자리가 **제 방**을 쓰는가, **기본 방**을 쓰는가 — 화면이 그렇게 말할 수 있게. */
export function meetSource(
  own: string | undefined | null,
  siteDefault: string | undefined | null,
): "own" | "default" | "none" {
  if (own?.trim()) return "own";
  if (siteDefault?.trim()) return "default";
  return "none";
}
